import React, { Component } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Container, Row, Col, Card, CardBody, Table, Label, Spinner, Button } from 'reactstrap'
import moment from 'moment'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { logoBase64 } from '../../../assets/images/logoBase64'
import Breadcrumbs from '../../../components/Common/Breadcrumb'
import Select from '../../../components/Template/Input/Select'
import { getMonthlyActivitiesReport, getHoursBank, getMonthlyWorkload } from '../../../store/ducks/humanResources'
import { getRegs } from '../../../utils/fc'
import { buildMonthSelectOptions } from '../../../utils/monthSelectOptions'
import { fetchExtratoRhHistorico } from '../Dashboard/rhDashboardApi'
import api from '../../../services/segi/index'
import { notifyError } from '../../../utils/notify'

// Mesma regra do backend: RhAtivEhTrabalho (extrato RH / dashboard)
const isHorasTrabalho = (activity) => {
  const desc = String(activity.pontoAtiv_desc || '')
  const ativ = String(activity.pontoAtiv_ativ || '')
  const isCredito = /CREDITO DE HORAS/i.test(desc) || /CREDITO DE HORAS/i.test(ativ)
  const isDispensa = ativ === 'Dispensa' || ativ === 'Ocioso'
  return !isCredito && !isDispensa
}

const activityWorkSeconds = (activity) => (
  isHorasTrabalho(activity) ? moment.duration(activity.pontoAtiv_tempo).asSeconds() : 0
)

class ActivitiesReport extends Component {
  constructor(props) {
    super(props)
    const { user } = this.props.auth
    this.state = {
      monthFilter: {
        value: moment().format('MM/YYYY'),
        label: moment().format('MM/YYYY'),
      },
      selectedUser: user ? {
        value: user.pf_graphId || user.id,
        label: user.displayName || user.name || user.pf_nomeExibicao || 'Usuário',
        pf_id: user.pf_id,
        pf_graphId: user.pf_graphId || user.id,
      } : null,
      colaboradores: [],
      loadingColaboradores: false,
      isExportingYear: false,
      monthOptions: [],
    }
  }

  loadMonthOptions = async (graphId) => {
    if (!graphId) return
    try {
      const data = await fetchExtratoRhHistorico(graphId)
      const meses = data && Array.isArray(data.meses) ? data.meses : []
      let options = meses
        .filter((m) => m && m.mes)
        .map((m) => ({
          value: m.mes,
          label: m.mes,
          mesRef: m.mes_ref || moment(m.mes, 'MM/YYYY').format('YYYY-MM'),
        }))

      const currentValue = moment().format('MM/YYYY')
      if (!options.some((o) => o.value === currentValue)) {
        options.unshift({
          value: currentValue,
          label: currentValue,
          mesRef: moment().format('YYYY-MM'),
        })
      }

      options.sort((a, b) => (a.mesRef < b.mesRef ? 1 : -1))
      this.setState({ monthOptions: options })
    } catch (err) {
      console.error('Erro ao carregar meses com atividade', err)
      this.setState({ monthOptions: [] })
    }
  }

  async componentDidMount() {
    const { getMonthlyActivitiesReport, getHoursBank, getMonthlyWorkload } = this.props
    const { user } = this.props.auth

    const initialUserId = user?.pf_graphId || user?.id
    getMonthlyActivitiesReport(moment().format('MM/YYYY'), initialUserId)
    getHoursBank(initialUserId)
    getMonthlyWorkload(initialUserId)
    this.loadMonthOptions(initialUserId)

    const ALLOWED_IDS = [1, 2, 3, 18, 104, 504, 639]
    const ALLOWED_EMAILS = [
      'gustavo.leite@ecossis.com.br',
      'juliano.moreira@ecossis.com.br',
      'bernardo.alcalde@ecossis.com.br',
      'tamiris.carvalho@ecossis.com.br',
      'ronaldo.pla@ecossis.com.br',
      'joao.casaroli@ecossis.com.br'
    ]

    const hasAccess = user && (
      ALLOWED_IDS.includes(Number(user.pf_id)) ||
      ALLOWED_EMAILS.includes(String(user.pf_emailProf || user.mail).toLowerCase())
    )

    if (hasAccess) {
      this.setState({ loadingColaboradores: true })
      try {
        const data = await getRegs('pf', { pf_ativo: 1 })
        
        let idsComContratoAtivo = new Set();
        try {
          const contratos = await getRegs('contratoForn', { 
            contratoForn_ativo: 1, 
            contratoForn_parteTab: 'pf'
          });
          if (contratos && Array.isArray(contratos)) {
            contratos.forEach(c => {
              if (c.contratoForn_parteId) {
                idsComContratoAtivo.add(String(c.contratoForn_parteId));
              }
            });
          }
        } catch (err) {
          console.error('Erro ao buscar contratos', err);
        }

        if (data && Array.isArray(data)) {
          let filtered = data.filter(u => u.pf_nomeExibicao && u.pf_graphId);

          filtered = filtered.filter(u => idsComContratoAtivo.has(String(u.pf_id)));

          filtered = filtered.map(u => ({
              value: u.pf_graphId,
              label: u.pf_nomeExibicao,
              pf_id: u.pf_id,
              pf_graphId: u.pf_graphId,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

          this.setState({ colaboradores: filtered })
        }
      } catch (err) {
        console.error('Error fetching collaborators:', err)
      } finally {
        this.setState({ loadingColaboradores: false })
      }
    }
  }

  handleUserChange = (selectedUser) => {
    const { getMonthlyActivitiesReport, getHoursBank, getMonthlyWorkload } = this.props
    const { monthFilter } = this.state
    this.setState({ selectedUser })
    if (selectedUser && selectedUser.pf_graphId) {
      getMonthlyActivitiesReport(monthFilter.value, selectedUser.pf_graphId)
      getHoursBank(selectedUser.pf_graphId)
      getMonthlyWorkload(selectedUser.pf_graphId)
      this.loadMonthOptions(selectedUser.pf_graphId)
    }
  }

  formatSeconds = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  calculateWorkDaySeconds = (workDay) => {
    if (!workDay) return 0
    return workDay.reduce((sum, activity) => sum + activityWorkSeconds(activity), 0)
  }

  calculateTotalMonthSeconds = (monthlyActivitiesReport) => {
    if (!monthlyActivitiesReport) return 0
    return monthlyActivitiesReport.reduce(
      (sum, workDay) => sum + this.calculateWorkDaySeconds(workDay),
      0,
    )
  }

  exportYearlyPDF = async () => {
    const { user } = this.props.auth
    const { monthFilter, selectedUser } = this.state

    this.setState({ isExportingYear: true })
    try {
      const year = monthFilter.value.split('/')[1]
      const targetUserId = selectedUser ? selectedUser.pf_graphId : user?.pf_graphId || user?.id

      let fullYearReport = []
      
      for (let m = 1; m <= 12; m++) {
        const monthString = `${String(m).padStart(2, '0')}/${year}`
        const formData = new FormData()
        formData.append('id', targetUserId)
        formData.append('acao', 'relatorio_atividades')
        formData.append('mes', monthString)

        try {
          const res = await api.post('/', formData, {
            headers: { 'Content-Type': 'multipart/form-data', Accept: 'multipart/form-data' }
          })
          if (res.data && Object.keys(res.data).length > 0) {
            fullYearReport = fullYearReport.concat(Object.values(res.data))
          }
        } catch (err) {
          console.error(`Erro ao buscar mês ${monthString}`, err)
        }
      }

      if (fullYearReport.length === 0) {
        notifyError('Nenhuma atividade encontrada no ano.');
        return;
      }

      const doc = new jsPDF()

      doc.addImage(logoBase64, 'PNG', 145, 8, 50, 10.4)

      doc.setFontSize(14)
      doc.text('Relatório de Atividades', 14, 20)

      doc.setFontSize(9)
      doc.setFont(undefined, 'bold')
      doc.text('Colaborador:', 14, 28)
      doc.setFont(undefined, 'normal')
      const fullName = selectedUser ? selectedUser.label : (user?.displayName || user?.name || `${user?.givenName || ''} ${user?.surname || ''}`.trim() || user?.nome || 'Usuário')
      doc.text(`${fullName}`, 36, 28)

      doc.setFont(undefined, 'bold')
      doc.text('Exportado em:', 14, 33)
      doc.setFont(undefined, 'normal')
      doc.text(`${moment().format('DD/MM/YYYY HH:mm')}`, 39, 33)

      doc.setFont(undefined, 'bold')
      doc.text('Período de referência:', 14, 38)
      doc.setFont(undefined, 'normal')
      doc.text(`01/01/${year} a 31/12/${year}`, 48, 38)

      let currentY = 43

      const summaryRows = []
      summaryRows.push(['Total Trabalhado no Ano', this.formatSeconds(this.calculateTotalMonthSeconds(fullYearReport))])
      summaryRows.push(['Dias Trabalhados no Ano', `${fullYearReport.length}`])

      doc.autoTable({
        body: summaryRows,
        startY: currentY,
        theme: 'grid',
        styles: { fontSize: 9, textColor: [0, 0, 0] },
        columnStyles: {
          0: { fontStyle: 'bold', fillColor: [248, 248, 248], cellWidth: 80 },
          1: { fontStyle: 'bold' }
        },
        tableLineColor: [230, 230, 230],
        tableLineWidth: 0.1,
      })

      currentY = (doc.lastAutoTable || doc.previousAutoTable).finalY + 15

      const tableColumn = ['Projeto', 'Atividade', 'Descrição', 'Tempo Dedicado']

      const reportByMonth = {};
      fullYearReport.forEach(workDay => {
         const monthKey = moment(workDay[0].pontoAtiv_dt).format('YYYY-MM');
         if (!reportByMonth[monthKey]) reportByMonth[monthKey] = [];
         reportByMonth[monthKey].push(workDay);
      });

      const sortedMonthKeys = Object.keys(reportByMonth).sort();

      sortedMonthKeys.forEach(monthKey => {
         const monthWorkDays = reportByMonth[monthKey];
         monthWorkDays.sort((a, b) => moment(a[0].pontoAtiv_dt).valueOf() - moment(b[0].pontoAtiv_dt).valueOf());

         const monthName = moment(monthKey, 'YYYY-MM').format('MMMM [de] YYYY').toUpperCase();
         const monthTotalSeconds = this.calculateTotalMonthSeconds(monthWorkDays);

         if (currentY > 240) {
           doc.addPage();
           currentY = 20;
         } else {
           currentY += 10;
         }

         doc.setFontSize(11);
         doc.setFont(undefined, 'bold');
         doc.setTextColor(0, 0, 0);
         doc.text(`${monthName} - TOTAL: ${this.formatSeconds(monthTotalSeconds)}`, 14, currentY);
         
         currentY += 10;

         monthWorkDays.forEach((workDay) => {
           const dateLabel = moment(workDay[0].pontoAtiv_dt).format('dddd, DD [de] MMMM [de] YYYY')
           const formattedDateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)

           if (currentY > 260) {
             doc.addPage()
             currentY = 20
           }

           doc.setFontSize(9)
           doc.setFont(undefined, 'normal')
           doc.setTextColor(0, 0, 0)
           doc.text(formattedDateLabel, 14, currentY)

           currentY += 4

           const tableRows = []
           const dayTotalSeconds = this.calculateWorkDaySeconds(workDay)

           workDay.forEach((activity) => {
             tableRows.push([
               activity.projetoNome,
               activity.pontoAtiv_ativ,
               activity.pontoAtiv_desc,
               activity.pontoAtiv_tempo,
             ])
           })

           tableRows.push([
             { content: 'Total', colSpan: 3, styles: { fontStyle: 'bold' } },
             { content: this.formatSeconds(dayTotalSeconds), styles: { fontStyle: 'bold' } },
           ])

           doc.autoTable({
             head: [tableColumn],
             body: tableRows,
             startY: currentY,
             styles: { fontSize: 9, textColor: [0, 0, 0] },
             headStyles: { fillColor: [248, 248, 248], textColor: [0, 0, 0], fontStyle: 'normal' },
             margin: { top: 20 },
             theme: 'grid',
             tableLineColor: [230, 230, 230],
             tableLineWidth: 0.1,
           })

           currentY = (doc.lastAutoTable || doc.previousAutoTable).finalY + 12
         })
      })

      const lastTable = doc.lastAutoTable || doc.previousAutoTable
      const finalY = (lastTable && lastTable.finalY) ? lastTable.finalY : currentY + 40

      let resultY = finalY + 10

      if (resultY > 270) {
        doc.addPage()
        resultY = 20
      }

      doc.setFontSize(11)
      doc.setFont(undefined, 'bold')
      doc.text(`Total Trabalhado no Ano: ${this.formatSeconds(this.calculateTotalMonthSeconds(fullYearReport))}`, 14, resultY)

      let signatureY = resultY + 20

      if (signatureY > 260) {
        doc.addPage()
        signatureY = 30
      }

      doc.setFontSize(10)
      doc.text('Declaro que as horas acima correspondem aos serviços prestados no periodo informado.', 14, signatureY)

      signatureY += 25

      doc.line(14, signatureY, 80, signatureY)
      doc.line(110, signatureY, 185, signatureY)

      signatureY += 5

      doc.text('Prestador de serviço', 14, signatureY)
      doc.text('Responsável / Contratante', 110, signatureY)

      const fileSuffix = fullName ? `_${fullName.toLowerCase().replaceAll(' ', '_')}` : '';
      doc.save(`relatorio_atividades_anual_${year}${fileSuffix}.pdf`)

    } finally {
      this.setState({ isExportingYear: false })
    }
  }

  exportPDF = () => {
    const { monthlyActivitiesReport, hoursBank, monthlyWorkload } = this.props.humanResources
    const { user } = this.props.auth
    const { monthFilter, selectedUser } = this.state
    if (!monthlyActivitiesReport || monthlyActivitiesReport.length === 0) return

    const doc = new jsPDF()

    doc.addImage(logoBase64, 'PNG', 145, 8, 50, 10.4)

    doc.setFontSize(14)
    doc.text('Relatório de Atividades', 14, 20)

    doc.setFontSize(9)
    doc.setFont(undefined, 'bold')
    doc.text('Colaborador:', 14, 28)
    doc.setFont(undefined, 'normal')
    const fullName = selectedUser ? selectedUser.label : (user?.displayName || user?.name || `${user?.givenName || ''} ${user?.surname || ''}`.trim() || user?.nome || 'Usuário')
    doc.text(`${fullName}`, 36, 28)

    doc.setFont(undefined, 'bold')
    doc.text('Exportado em:', 14, 33)
    doc.setFont(undefined, 'normal')
    doc.text(`${moment().format('DD/MM/YYYY HH:mm')}`, 39, 33)

    doc.setFont(undefined, 'bold')
    doc.text('Período de referência:', 14, 38)
    doc.setFont(undefined, 'normal')
    const startOfMonth = moment(monthFilter.value, 'MM/YYYY').startOf('month').format('DD/MM/YYYY')
    const endOfMonth = moment(monthFilter.value, 'MM/YYYY').endOf('month').format('DD/MM/YYYY')
    doc.text(`${startOfMonth} a ${endOfMonth}`, 48, 38)

    let currentY = 43

    let cargaHorariaMensal = hoursBank && hoursBank[monthFilter.value] ? hoursBank[monthFilter.value].carga_horaria : null

    if (!cargaHorariaMensal && hoursBank) {
      const keys = Object.keys(hoursBank).filter(k => k.includes('/'))
      if (keys && keys.length > 0) {
        cargaHorariaMensal = hoursBank[keys[keys.length - 1]].carga_horaria
      }
    }

    const saldoGeralBanco = hoursBank && hoursBank.banco_acumulado_timestamp2 ? hoursBank.banco_acumulado_timestamp2 : null

    const summaryRows = []
    summaryRows.push(['Total Trabalhado no Mês', this.formatSeconds(this.calculateTotalMonthSeconds(monthlyActivitiesReport))])
    if (monthlyActivitiesReport && monthlyActivitiesReport.length > 0) {
      summaryRows.push(['Dias Trabalhados no Mês', `${monthlyActivitiesReport.length}`])
    }
    if (cargaHorariaMensal) summaryRows.push(['Horas Contratuais (Mês)', cargaHorariaMensal])
    if (saldoGeralBanco) summaryRows.push(['Saldo Geral de Banco de Horas', saldoGeralBanco])

    doc.autoTable({
      body: summaryRows,
      startY: currentY,
      theme: 'grid',
      styles: { fontSize: 9, textColor: [0, 0, 0] },
      columnStyles: {
        0: { fontStyle: 'bold', fillColor: [248, 248, 248], cellWidth: 80 },
        1: { fontStyle: 'bold' }
      },
      tableLineColor: [230, 230, 230],
      tableLineWidth: 0.1,
    })

    currentY = (doc.lastAutoTable || doc.previousAutoTable).finalY + 15

    const tableColumn = ['Projeto', 'Atividade', 'Descrição', 'Tempo Dedicado']

    const sortedReport = [...monthlyActivitiesReport].sort((a, b) => moment(a[0].pontoAtiv_dt).valueOf() - moment(b[0].pontoAtiv_dt).valueOf())

    sortedReport.forEach((workDay) => {
      const dateLabel = moment(workDay[0].pontoAtiv_dt).format('dddd, DD [de] MMMM [de] YYYY')
      const formattedDateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)

      if (currentY > 260) {
        doc.addPage()
        currentY = 20
      }

      doc.setFontSize(9)
      doc.setFont(undefined, 'normal')
      doc.setTextColor(0, 0, 0)
      doc.text(formattedDateLabel, 14, currentY)

      currentY += 4

      const tableRows = []
      const dayTotalSeconds = this.calculateWorkDaySeconds(workDay)

      workDay.forEach((activity) => {
        tableRows.push([
          activity.projetoNome,
          activity.pontoAtiv_ativ,
          activity.pontoAtiv_desc,
          activity.pontoAtiv_tempo,
        ])
      })

      tableRows.push([
        { content: 'Total', colSpan: 3, styles: { fontStyle: 'bold' } },
        { content: this.formatSeconds(dayTotalSeconds), styles: { fontStyle: 'bold' } },
      ])

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: currentY,
        styles: { fontSize: 9, textColor: [0, 0, 0] },
        headStyles: { fillColor: [248, 248, 248], textColor: [0, 0, 0], fontStyle: 'normal' },
        margin: { top: 20 },
        theme: 'grid',
        tableLineColor: [230, 230, 230],
        tableLineWidth: 0.1,
      })

      currentY = (doc.lastAutoTable || doc.previousAutoTable).finalY + 12
    })

    const lastTable = doc.lastAutoTable || doc.previousAutoTable
    const finalY = (lastTable && lastTable.finalY) ? lastTable.finalY : currentY + 40

    let resultY = finalY + 10

    if (resultY > 270) {
      doc.addPage()
      resultY = 20
    }

    doc.setFontSize(11)
    doc.setFont(undefined, 'bold')
    doc.text(`Total Trabalhado no Mês: ${this.formatSeconds(this.calculateTotalMonthSeconds(monthlyActivitiesReport))}`, 14, resultY)

    let signatureY = resultY + 20

    // Se estiver muito perto do fim da página, cria uma nova página para as assinaturas
    if (signatureY > 260) {
      doc.addPage()
      signatureY = 30
    }

    doc.setFontSize(10)
    doc.text('Declaro que as horas acima correspondem aos serviços prestados no periodo informado.', 14, signatureY)

    signatureY += 25

    doc.line(14, signatureY, 80, signatureY)
    doc.line(110, signatureY, 185, signatureY)

    signatureY += 5

    doc.text('Prestador de serviço', 14, signatureY)
    doc.text('Responsável / Contratante', 110, signatureY)

    const fileSuffix = fullName ? `_${fullName.toLowerCase().replaceAll(' ', '_')}` : '';
    doc.save(`relatorio_atividades_${monthFilter.label.replace('/', '_')}${fileSuffix}.pdf`)
  }

  render() {
    const { getMonthlyActivitiesReport } = this.props
    const {
      monthlyActivitiesReport, getMonthlyActivitiesIsLoading, getMonthlyActivitiesReportIsLoading, hoursBank
    } = this.props.humanResources
    const { monthFilter, selectedUser, colaboradores, loadingColaboradores } = this.state
    const { user } = this.props.auth

    let cargaHorariaMensal = hoursBank && hoursBank[monthFilter.value] ? hoursBank[monthFilter.value].carga_horaria : null

    if (!cargaHorariaMensal && hoursBank) {
      const keys = Object.keys(hoursBank).filter(k => k.includes('/'))
      if (keys && keys.length > 0) {
        cargaHorariaMensal = hoursBank[keys[keys.length - 1]].carga_horaria
      }
    }

    const saldoGeralBanco = hoursBank && hoursBank.banco_acumulado_timestamp2 ? hoursBank.banco_acumulado_timestamp2 : null

    if (getMonthlyActivitiesReportIsLoading) {
      return (
        <div className="centered">
          <Spinner />
        </div>
      )
    }

    const ALLOWED_IDS = [1, 2, 3, 18, 104, 504]
    const ALLOWED_EMAILS = [
      'gustavo.leite@ecossis.com.br',
      'juliano.moreira@ecossis.com.br',
      'bernardo.alcalde@ecossis.com.br',
      'tamiris.carvalho@ecossis.com.br',
      'ronaldo.pla@ecossis.com.br',
      'joao.casaroli@ecossis.com.br',
      'joaopedrorc243@gmail.com'
    ]

    const hasAccess = user && (
      ALLOWED_IDS.includes(Number(user.pf_id)) ||
      ALLOWED_EMAILS.includes(String(user.pf_emailProf || user.mail).toLowerCase())
    )

    const collaboratorName = selectedUser ? selectedUser.label : (user?.displayName || user?.name || user?.pf_nomeExibicao || 'Usuário')

    return (
      <div className="page-content">
        <Container fluid>

          <Row>
            <Col>
              <Breadcrumbs
                title="Relatório de atividades"
                breadcrumbItems={[]}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <h4 className="text-muted">
                Colaborador: {collaboratorName}
              </h4>
            </Col>
          </Row>

          {hasAccess && (
            <Row style={{ marginBottom: 20 }}>
              <Col xl={3} lg={3} md={3} sm={3}>
                <Label>Filtrar por Colaborador</Label>
                <Select
                  isClearable={false}
                  isSearchable={true}
                  placeholder="Selecione o Colaborador..."
                  value={selectedUser}
                  options={colaboradores}
                  onChange={this.handleUserChange}
                  isLoading={loadingColaboradores}
                />
              </Col>
            </Row>
          )}

          <Row style={{ marginBottom: 40, alignItems: 'flex-end' }}>
            <Col xl={3} lg={3} md={3} sm={3}>
              <Label>
                Filtrar por Mês
              </Label>
              <Select
                isClearable={false}
                isSearchable={false}
                isDisabled={getMonthlyActivitiesIsLoading}
                isLoading={getMonthlyActivitiesIsLoading}
                placeholder="Mês"
                value={{
                  label: monthFilter.label,
                  value: monthFilter.value,
                }}
                options={(this.state.monthOptions && this.state.monthOptions.length > 0
                  ? this.state.monthOptions
                  : buildMonthSelectOptions(12)
                ).map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                onChange={(monthFilter) => {
                  this.setState({ monthFilter })
                  const { selectedUser } = this.state
                  const userId = selectedUser ? selectedUser.pf_graphId : this.props.auth.user?.pf_graphId || this.props.auth.user?.id
                  getMonthlyActivitiesReport(monthFilter.value, userId)
                }}
              />
            </Col>
            {monthlyActivitiesReport && monthlyActivitiesReport.length > 0 && (
              <Col xl={9} lg={9} md={9} sm={9} className="d-flex justify-content-end">
                <Button color="primary" onClick={this.exportPDF} className="mr-2">
                  <i className="mdi mdi-download mr-1" /> Exportar Mês para PDF
                </Button>
                <Button color="primary" onClick={this.exportYearlyPDF} disabled={this.state.isExportingYear}>
                  {this.state.isExportingYear ? <Spinner size="sm" className="mr-1" /> : <i className="mdi mdi-download mr-1" />}
                  Exportar Ano para PDF
                </Button>
              </Col>
            )}
          </Row>

          {!monthlyActivitiesReport && (
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <p className="text-center mb-0 p-2">
                      Não foram encontradas atividades para o período selecionado.
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            <Col>
              {monthlyActivitiesReport && monthlyActivitiesReport.length > 0 && (
                <>
                  <Card className="mb-4">
                    <Table responsive striped bordered className="mb-0">
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: 'bold' }}>Total trabalhado no mês</td>
                          <td style={{ fontWeight: 'bold', width: '20%' }}>
                            {this.formatSeconds(this.calculateTotalMonthSeconds(monthlyActivitiesReport))}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card>

                  {[...monthlyActivitiesReport]
                    .sort((a, b) => moment(a[0].pontoAtiv_dt).valueOf() - moment(b[0].pontoAtiv_dt).valueOf())
                    .map((workDay, index) => (
                      <div key={index}>
                        <Label>
                          {moment(workDay[0].pontoAtiv_dt).format('dddd, DD [de] MMMM [de] YYYY')}
                        </Label>
                        <Card className="p-0 mb-5">
                          <Table responsive striped bordered style={{ marginBottom: 0 }}>
                            <thead>
                              <tr>
                                <th>Projeto</th>
                                <th>Atividade</th>
                                <th>Descrição</th>
                                <th>Tempo dedicado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {workDay.map((activity, actIndex) => (
                                <tr key={actIndex}>
                                  <td>{activity.projetoNome}</td>
                                  <td>{activity.pontoAtiv_ativ}</td>
                                  <td>{activity.pontoAtiv_desc}</td>
                                  <td>{activity.pontoAtiv_tempo}</td>
                                </tr>
                              ))}
                              <tr>
                                <td style={{ fontWeight: 'bold' }}>Total</td>
                                <td />
                                <td />
                                <td style={{ fontWeight: 'bold' }}>
                                  {this.formatSeconds(this.calculateWorkDaySeconds(workDay))}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card>
                      </div>
                    ))}
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  humanResources: state.humanResources,
  auth: state.auth
})
const mapDispatchToProps = dispatch => bindActionCreators({
  getMonthlyActivitiesReport,
  getHoursBank,
  getMonthlyWorkload,
}, dispatch)
export default connect(mapStateToProps, mapDispatchToProps)(ActivitiesReport)
